import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';

import THEME from '../constants/theme';
import ROUTES from '../config/routes';

const NotFoundPage = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate(ROUTES.HOME);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6511af 0%, #8e44ad 50%, #9b59b6 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              textAlign: 'center',
              color: 'white',
              padding: '2rem'
            }}
          >
            {/* Large 404 Number */}
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '8rem', md: '12rem' },
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                  lineHeight: 1,
                  background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 4px 20px rgba(255, 255, 255, 0.3)',
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
                }}
              >
                404
              </Typography>
            </motion.div>

            {/* Error Message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Typography
                variant="h4"
                sx={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
                }}
              >
                Oops! Page Not Found
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  marginBottom: '3rem',
                  opacity: 0.9,
                  maxWidth: '600px',
                  margin: '0 auto 3rem auto',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
                }}
              >
                The page you're looking for seems to have vanished into the digital ocean. 
                Let's get you back on track!
              </Typography>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  onClick={handleGoHome}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Go Home
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<SearchIcon />}
                  onClick={handleGoBack}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.6)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Go Back
                </Button>
              </Box>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default NotFoundPage;
